// The agent LOOP renderer family for the Terminal console (#4435, epic #4391).
//
// One entry per `AUDIT_KIND_LOOP` kind (`../../audit/kind-loop.ts`):
// session boot, model calls, tool + MCP-tool calls, the policy gate, the
// escalation/handoff pair and its mail leg, and the BYOK-key + billing-
// preference + account lifecycle that money gates the loop on. Read
// `../../../wire/terminal.ts` in full before editing — it is the contract this
// file fills, not restated here.
//
// ─── WHY THE `render` BODIES ARE NAMED FUNCTIONS, NOT INLINE ENTRY OBJECTS ───
//
// Measured 2026-09-18, not a style choice: assigning a fully-typed
// `const x: TerminalRendererEntry = {...}` and then referencing `x` inside the
// `terminalRendererGroup({...})` call fails composite declaration emission —
// `tsc` cannot print the exported group's inferred type without naming
// `TerminalRendererShape`, which `wire/terminal.ts` deliberately does not
// export (TS4023). Passing the `{ level, actor, render }` wrapper as an
// INLINE object literal, with only the `render` function pulled out by name,
// compiles clean — contextual typing against `TerminalRendererEntry` at each
// property position resolves the alias, where a prior variable binding does
// not. So every kind below is `render<KindName>` (a plain function, typed by
// its parameter) referenced from an inline wrapper in the final export.
//
// ─── WHY MOST SUBJECTS FALL BACK TO THE SESSION ──────────────────────────────
//
// `TerminalRow` (`wire/terminal.ts`) carries no task, PR or issue id — the
// actor/subject refinement that would let a renderer point at something
// sharper is explicitly DEFERRED there (see that file's `TerminalActor` doc).
// The one id every row in this family reliably has is its own session, so
// `subjectForRow` below is "the row's repo if it has one, else its session",
// and every non-MCP entry uses it.
//
// ─── ACTOR SPLIT ──────────────────────────────────────────────────────────────
//
// Kinds intrinsic to a coding agent's own loop (boot, model calls, native tool
// calls) declare `coder` — the classifier's stated default ("review ->
// reviewer, babysit -> babysitter, interactive -> helper, else coder",
// `wire/terminal.ts`), and `project.ts` overrides to `human` whenever the row
// carries a `userId` regardless of what is declared here. `mcp_tool.call.*`
// declares `mcp` per the contract. Everything past the loop proper — the
// policy gate, escalation/handoff, mail, and the billing/account
// lifecycle — is system-lane machinery fired by dispatch/worker code, not the
// agent choosing to act, so those declare `system`.
//
// ─── OMIT DECISIONS, EACH WITH ITS OWN REASON AT ITS OWN KEY ─────────────────
//
// `model.step`, `model.reasoning`, `billing.metered`, `billing.key.limits_changed`,
// `billing.llm_pref.set`, `billing.llm_pref.cleared` and
// `account.seats_synced` are `'omit'`. Grep this file for `'omit'` rather than
// trusting this summary as the vocabulary grows — each has its own one-line
// reason inline.

import {
  attemptParams,
  type TerminalFailRenderContext,
  type TerminalFailRendered,
  type TerminalRenderContext,
  type TerminalRendered,
  type TerminalRow,
  type TerminalSubject,
  terminalRendererGroup,
} from '../../../wire/terminal';
import { TERMINAL_RENDERERS_BILLING_OWNERSHIP } from './loop-billing-ownership';
import { TERMINAL_RENDERERS_KEY_MODELS } from './loop-key-models';

// ─── shared payload helpers ───────────────────────────────────────────────────
//
// `row.input` / `row.output` are `unknown` (the query's own honesty — see
// `TerminalRow`'s doc), so every field read below is defensive: a shape this
// file has not seen yet renders the honest fallback rather than throwing.

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
}

function strOr(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function numOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/** A field off `row.input`, string-or-fallback. */
function inField(row: TerminalRow, key: string, fallback: string): string {
  return strOr(asRecord(row.input)?.[key], fallback);
}

/** A field off `row.output`, string-or-fallback. */
function outField(row: TerminalRow, key: string, fallback: string): string {
  return strOr(asRecord(row.output)?.[key], fallback);
}

/**
 * The subject every non-MCP entry falls back to: the row's repo when it has
 * one (a repo-bound session — the common case for the loop family), else the
 * row's own session, which is the one id `TerminalRow` always carries.
 */
function subjectForRow(row: TerminalRow): TerminalSubject {
  if (row.repoFullName !== null) {
    return { kind: 'repo', id: row.repoFullName, label: row.repoFullName };
  }
  return { kind: 'session', id: row.sessionId, label: row.sessionId };
}

/** The session subject, unconditionally — what every `mcp_tool.call.*` entry uses. */
function sessionSubject(row: TerminalRow): TerminalSubject {
  return { kind: 'session', id: row.sessionId, label: row.sessionId };
}

/**
 * A tool-call error's reason. Writers of `tool.call.err` / `mcp_tool.call.err`
 * do not agree on the field: most carry `output.error` (a message string,
 * `packages/agent/src/loop/audit.ts:143`, `tool-call-repair.ts`,
 * `approvals-audit.ts`), one gateway MCP door carries only `output.status`
 * (`integration-slack-post.ts`). Try both before falling back.
 */
function toolErrorReason(row: TerminalRow): string {
  const out = asRecord(row.output);
  const error = out?.['error'];
  if (typeof error === 'string' && error.length > 0) return error;
  const status = out?.['status'];
  if (typeof status === 'string' && status.length > 0) return status;
  return 'unknown error';
}

// ─── session boot, model calls ────────────────────────────────────────────────

/**
 * `session.boot` — the loop's own boot fingerprint (`emitSessionBoot`,
 * `packages/agent/src/session.ts:324`): trigger, provider, model. One writer
 * (`byok-reverify.lane.ts`) reuses this SAME kind for its per-pass sweep
 * summary instead (`input.decision: 'byok_reverify_swept'`, no `trigger`) —
 * checking for `decision` first renders that shape honestly rather than
 * misreading it as a real agent boot.
 */
function renderSessionBoot(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const decision = asRecord(row.input)?.['decision'];
  if (typeof decision === 'string') {
    return {
      line: { key: 'terminal.session.boot', params: { decision } },
      subject: sessionSubject(row),
      refs: { sessionId: row.sessionId },
    };
  }
  return {
    line: {
      key: 'terminal.session.boot',
      params: {
        trigger: inField(row, 'trigger', 'unknown'),
        provider: inField(row, 'provider', 'unknown'),
        model: inField(row, 'model', 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `model.call` — one attempt of a model invocation (`classifier-port.ts`,
 * `generate-post.ts`). `purpose` distinguishes the classifier leg from a
 * main-loop call; `status` is the attempt's outcome.
 */
function renderModelCall(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.model.call',
      params: {
        purpose: inField(row, 'purpose', 'loop'),
        model: inField(row, 'model', 'unknown'),
        status: outField(row, 'status', 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `llm.failover` (plan 102 slice 06a) — the session's key failed mid-run and it
 * moved to the next pool key rather than failing. Reads `output`
 * (`LlmFailoverOutputSchema`, `../../audit/payload`). `decision`: the platform
 * chose a different credential for the customer's run, which is the line an
 * operator scanning for "why did this run change provider" looks for. A row
 * missing either key or the reason renders nothing: a defaulted key would name a
 * hop the run never made.
 */
function renderLlmFailover(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const out = asRecord(row.output);
  const from = out?.['from_key'];
  const to = out?.['to_key'];
  const reason = out?.['reason'];
  if (typeof from !== 'string' || typeof to !== 'string' || typeof reason !== 'string') {
    return null;
  }
  return {
    line: { key: 'terminal.llm.failover', params: { from, to, reason } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// `model.reasoning`: OMIT. The row's own `reasoning` text — the only content
// this kind could show — is not on `TerminalRow` at all (the terminal query
// selects `id, ts, kind, tool, sessionId, userId, input, output,
// visibleToMaintainer, repoFullName`; no `reasoning` column). A rendered line
// with `model.call` already covering the same attempt and nothing new to say
// would be noise on every turn of every loop — the densest kind in this
// family by volume. Its one production writer (`packages/agent`
// `loop/model-step.ts`, plan 101 slice 02) also writes a row the secret scan
// flags `visibleToMaintainer: false` — held for manual review, not a console.

// `model.step`: OMIT. One row per in-cluster session step (`packages/agent`
// `loop/model-step.ts`) — model id, token counts, finish reason. Rendering it
// would put a line between every tool call of every session, and what a reader
// needs from a step is already on the timeline: the tool rows it produced, and
// the terminal row with the totals. Its one decision-shaped field, a doom-loop
// `trip`, surfaces as the session's `tool_failure` escalation row.

// ─── native tool calls ────────────────────────────────────────────────────────

function renderToolCallPending(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.tool.call.pending', params: { tool: strOr(row.tool, 'unknown') } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderToolCallOk(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.tool.call.ok', params: { tool: strOr(row.tool, 'unknown') } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderToolCallErr(ctx: TerminalFailRenderContext): TerminalFailRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.tool.call.err',
      params: {
        ...attemptParams(ctx.attempt),
        tool: strOr(row.tool, 'unknown'),
        reason: toolErrorReason(row),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// ─── MCP tool calls ────────────────────────────────────────────────────────────
//
// Subject is the session, unconditionally, per this family's brief — an MCP
// call is a call an EXTERNAL agent made against the platform through the
// session's own channel, not against a repo the session happens to be bound
// to (a box-authenticated Slack post has no repo at all,
// `integration-slack-post.ts`).

function renderMcpToolCallPending(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.mcp_tool.call.pending', params: { tool: strOr(row.tool, 'unknown') } },
    subject: sessionSubject(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderMcpToolCallOk(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.mcp_tool.call.ok', params: { tool: strOr(row.tool, 'unknown') } },
    subject: sessionSubject(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderMcpToolCallErr(ctx: TerminalFailRenderContext): TerminalFailRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.mcp_tool.call.err',
      params: {
        ...attemptParams(ctx.attempt),
        tool: strOr(row.tool, 'unknown'),
        reason: toolErrorReason(row),
      },
    },
    subject: sessionSubject(row),
    refs: { sessionId: row.sessionId },
  };
}

// ─── the policy gate ───────────────────────────────────────────────────────────

/**
 * `policy.gate.refused` — the ONE sanctioned refusal kind, written from at
 * least five distinct call sites with five distinct payload shapes
 * (`mothership.ts` repo-activation, `stack-links-gate.ts` PR
 * projection, `task-dispatcher.ts` sink capability, `registry.ts`
 * tool capability, `review/wiring.ts` review entitlement — all read via grep,
 * 2026-09-18). They share exactly two fields worth showing: `input.gate` (a
 * few call sites) or `input.capability`/`input.toolName` as the closest
 * substitute, and `input.reason`. Rendered as `decision` — the vocabulary's
 * own level for a policy verdict, per `wire/terminal.ts`'s `TerminalLevel` doc.
 */
function renderPolicyGateRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const rec = asRecord(row.input);
  const gate = strOr(
    rec?.['gate'],
    strOr(rec?.['capability'], strOr(rec?.['toolName'], 'unknown')),
  );
  return {
    line: {
      key: 'terminal.policy.gate.refused',
      params: { gate, reason: strOr(rec?.['reason'], 'refused') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// ─── escalation / handoff / mail ───────────────────────────────────────────────

/**
 * `escalation.fired` (`packages/agent/src/escalation/dispatch.ts:414`):
 * category + channel are on every writer's `input`, whichever of the several
 * call sites fired it (`apps/api/src/index.ts`, `blocker-expiry.lane.ts`,
 * `babysit/wiring.ts`, `nats-consumers.ts`).
 */
function renderEscalationFired(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.escalation.fired',
      params: {
        category: inField(row, 'category', 'unknown'),
        channel: inField(row, 'channel', 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `handoff.fired` (`apps/api/src/index.ts`, three outcomes: `no_webhook`,
 * `already_pending`, `queued`) — `output.status` carries the outcome on every
 * call site.
 */
function renderHandoffFired(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.handoff.fired',
      params: {
        reason: inField(row, 'reason', 'unknown'),
        status: outField(row, 'status', 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `email.sent` (`stripe-events.lane.ts`, `agents-manager/wiring.ts`,
 * `key-verified.lane.ts`) — `input.template` is
 * on every writer. The escalation dispatcher stopped writing it in #5689 (its routed
 * mail leg is deleted); its old rows still render through this renderer.
 */
function renderEmailSent(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.email.sent', params: { template: inField(row, 'template', 'unknown') } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `email.route_missing` — the counterpart of `email.sent`, at `warn` (the fix is the
 * maintainer's). One writer now: owner mail (`ownerMailTargets`, no category; carries the
 * resolver's `reason`, the `purpose` and the `template`). Rows written by the escalation
 * leg (carries `category`) have no writer since #5689 (the routed leg is deleted) but
 * still render, each read in its own line.
 */
function renderEmailRouteMissing(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const ownerMail =
    asRecord(row.input)?.['category'] === undefined && inField(row, 'reason', '') !== '';
  return {
    line: ownerMail
      ? {
          key: 'terminal.email.no_recipient',
          params: {
            template: inField(row, 'template', 'unknown'),
            purpose: inField(row, 'purpose', 'unknown'),
            reason: inField(row, 'reason', 'unknown'),
          },
        }
      : {
          key: 'terminal.email.route_missing',
          params: { category: inField(row, 'category', 'unknown') },
        },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `email.failed` (CodeRabbit on #5815): one recipient's send threw at the transport.
 * `warn`: a member was not told. The address is not shown; the template and the
 * transport's reason are what a reader acts on.
 */
function renderEmailFailed(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.email.failed',
      params: {
        template: inField(row, 'template', 'unknown'),
        reason: inField(row, 'reason', 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `notification.suppressed` (plan 101 N1, the `notify.fanout` lane) — a recipient
 * the fan-out will stop mailing was newly recorded. `warn`: a member has stopped
 * receiving mail and the maintainer owns the remedy.
 */
function renderNotificationSuppressed(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.notification.suppressed',
      params: { reason: inField(row, 'reason', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// ─── account pause / resume / seats ────────────────────────────────────────────

/**
 * `account.paused` (`stripe-events.lane.ts` payment_failed,
 * `agents-manager/wiring.ts` credits_exhausted / byok_invalid) — the state
 * change that stops dispatch on the account, explicitly named worth rendering
 * by the executor brief.
 */
function renderAccountPaused(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: { key: 'terminal.account.paused', params: { reason: inField(row, 'reason', 'unknown') } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `account.resumed` (`stripe-events.lane.ts`, `byok-provider-reconcile.lane.ts`)
 * — the CAS that lets dispatch run again; the counterpart of `account.paused`.
 */
function renderAccountResumed(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.account.resumed',
      params: { reason: inField(row, 'reason', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// `billing.metered`: OMIT. The runner-minute lanes' per-tick summaries
// (`runner-minutes-report.lane.ts`, `runner-minutes-reconcile.lane.ts`) are the
// reporter draining its ledger on a schedule — pure system bookkeeping an operator
// never acts on, with nothing distinguishing one tick's row from the last.

// ─── BYOK key lifecycle ────────────────────────────────────────────────────────
//
// All seven `billing.key.*` entries below read `input.provider` — the one
// field `buildBillingAudit` (`apps/api/src/deps/billing-audit.ts:88`) and the
// `byok-reverify` lane's own port both always set, since the fields spread
// verbatim from `BillingAudit`'s discriminated union (`billing.ts`).

function renderBillingKeyCaptured(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.captured',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderBillingKeyRevoked(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.revoked',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderBillingKeyVerified(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.verified',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.invalid` — a credential moved to the human-only `invalid`
 * state, written by BOTH the operator-triggered verify/rotate door
 * (`billing.ts`,`:1939`) and the unattended `cron.byok_reverify` lane
 * (`byok-reverify.lane.ts`, which also stamps `reason`/`previousStatus`).
 * `warn`: named worth rendering by the executor brief, and it is the row that
 * explains why an account's runs stopped.
 */
function renderBillingKeyInvalid(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.invalid',
      params: {
        provider: inField(row, 'provider', 'unknown'),
        reason: inField(row, 'reason', 'rejected'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.transport_failed` (#1515) — a TRANSPORT failure (network/5xx),
 * deliberately distinct from `key.invalid`: the provider never rejected the
 * credential. `warn` because the account's key is unusable in the meantime,
 * even though the fix is "wait for auto-reverify", not "replace the key".
 */
function renderBillingKeyTransportFailed(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.transport_failed',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.model_parked` — one of a key's models parked, the credential still
 * `valid`. Two writers (the kind's own doc in `kind-loop.ts`): the verify-time
 * correctness park (#2844: the credential verified, the model could not answer) and
 * the dated capacity park (2026-10-17: a quota wall on that model alone, carrying
 * `output.until`). `warn`, not `fail`: the credential was never the problem, but a
 * parked model is capacity an operator lost and may want to look at.
 *
 * ROUTED BY THE PROBE FAULT the row carries (`input.fault`, #6657). The verify
 * park's outcome is `model_parked` BY RULING: it is the same outcome whether the
 * model could not answer or OUR egress gate refused its endpoint
 * (`egress_refused`, #3961 — the model was NEVER asked). An undifferentiated
 * line would blame the provider for a refusal that was OURS.
 *
 * So an `egress_refused` row renders the GATE's line, naming the gate and the
 * refusal it applied, and every other fault — and a row that states no fault at
 * all (the dated capacity park, whose writer reports none) — renders the plain
 * model-park line, unchanged.
 *
 * The gate line is the EXISTING `terminal.policy.gate.refused` key: our egress
 * gate IS a policy gate, and reusing that key keeps the row inside the shipped
 * catalogue (`apps/dashboard`'s `i18n.coverage.test.ts` requires every renderer
 * key to resolve under `en.json`). The console renders the line's TEXT, not its
 * key, so the reader sees one sentence: "policy gate egress refused: forbidden".
 *
 * The fault is read off `input` as STRUCTURED DATA — never parsed out of the
 * reason sentence — and routed through {@link PROBE_FAULT_ROUTE}, the one
 * routing over the fault vocabulary: a kind the vocabulary does not carry (or a
 * row that states none) keeps the model-park line, because guessing "our gate"
 * on a kind we cannot name would blame us for a provider's outage.
 */
// ─── the probe-fault vocabulary, and its one routing ──────────────────────
//
// The faults a `billing.key.model_parked` row can carry (#6657). The PRODUCER
// is `probeFields` (`apps/api/src/deps/key-audit.ts`), whose switch over
// `@developerz/billing`'s `StructuredProbeFault` is exhaustive with a `never`
// arm, and `apps/api/src/deps/key-audit.test.ts` pins that set against a
// `Record<StructuredProbeFault, …>`. This package imports nothing internal, so
// the producer's union cannot be imported — THIS list is the terminal's own
// statement of the same set, and a fault kind that lands in the probe must be
// added here to be routable (a kind nobody routed renders the model-park line).

export const PROBE_FAULT_KINDS = [
  'unreachable',
  'bad_shape',
  'quota',
  'auth',
  'egress_refused',
] as const;

export type ProbeFaultKind = (typeof PROBE_FAULT_KINDS)[number];

/** Which line a fault kind renders: OUR egress gate's, or the model park's. */
type ProbeFaultRoute = 'gate' | 'model';

/**
 * The ONE routing of a fault kind, EXHAUSTIVE BY COMPILER: a kind added to
 * {@link PROBE_FAULT_KINDS} without a row here fails to compile, and a row
 * naming a kind the vocabulary does not carry fails too. The renderer looks the
 * row's fault up here, and the walk in `./loop.test.ts` is driven by
 * {@link PROBE_FAULT_KINDS} itself, so the guard and the routing cannot drift
 * apart the way a hand-copied list would.
 */
const PROBE_FAULT_ROUTE: Record<ProbeFaultKind, ProbeFaultRoute> = {
  unreachable: 'model',
  bad_shape: 'model',
  quota: 'model',
  auth: 'model',
  egress_refused: 'gate',
};

function renderBillingKeyModelParked(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const fault = inField(row, 'fault', '');
  const known = PROBE_FAULT_KINDS.find((kind) => kind === fault);
  if (known !== undefined && PROBE_FAULT_ROUTE[known] === 'gate') {
    return {
      line: {
        key: 'terminal.policy.gate.refused',
        params: {
          gate: 'egress',
          reason: inField(row, 'refusal', 'refused'),
        },
      },
      subject: subjectForRow(row),
      refs: { sessionId: row.sessionId },
    };
  }
  return {
    line: {
      key: 'terminal.billing.key.model_parked',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

function renderBillingKeyRotated(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.key.rotated',
      params: { provider: inField(row, 'provider', 'unknown') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.kind_changed` (#1247) — the key's billing nature flipped
 * (`metered <-> subscription`), crossing a billing boundary either way
 * (`billing.ts`). `info`, not `omit`: it is an operator-initiated action
 * with a clean before/after on the row, unlike the `_synced`/`_set` sweep
 * kinds this family omits.
 */
function renderBillingKeyKindChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const rec = asRecord(row.input);
  return {
    line: {
      key: 'terminal.billing.key.kind_changed',
      params: {
        provider: strOr(rec?.['provider'], 'unknown'),
        from: strOr(rec?.['previous_kind'], 'none'),
        to: strOr(rec?.['next_kind'], 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.status_changed` (plan 102 slice 01) — `byok_keys.status` moved.
 * Reads `output` (`BillingKeyStatusChangedOutputSchema`, `../../audit/payload`),
 * not `input`: the transition IS the row's result. `info`, not `warn`: a static
 * level cannot tell a recovery (`rate_limited -> valid`) from a loss, and the
 * `to` param already says which. No `to` → nothing: a line that cannot name the
 * destination status has nothing to say.
 */
function renderBillingKeyStatusChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const out = asRecord(row.output);
  const to = out?.['to'];
  if (typeof to !== 'string') return null;
  return {
    line: {
      key: 'terminal.billing.key.status_changed',
      params: {
        from: strOr(out?.['from'], 'none'),
        to,
        reason: strOr(out?.['reason'], 'unknown'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.priority_changed` (plan 102 slice 05b) — an operator reordered the
 * pool. Reads `output` (`BillingKeyPriorityChangedOutputSchema`, `../../audit/payload`):
 * both ends are integers on every writer. A row missing either end renders nothing:
 * a defaulted `from` would claim a move from a position the key never held.
 */
function renderBillingKeyPriorityChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const out = asRecord(row.output);
  const from = out?.['from'];
  const to = out?.['to'];
  if (typeof from !== 'number' || typeof to !== 'number') return null;
  return {
    line: {
      key: 'terminal.billing.key.priority_changed',
      params: { from, to },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.capacity_changed` — an operator declared (or cleared) how many model
 * streams a key sustains at once. Reads `output`
 * (`BillingKeyCapacityChangedOutputSchema`): each end is an integer or `null`
 * (undeclared). A row whose ends are neither renders nothing, never a guess.
 */
function renderBillingKeyCapacityChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const out = asRecord(row.output);
  const from = out?.['from'];
  const to = out?.['to'];
  const isEnd = (v: unknown): v is number | null => v === null || typeof v === 'number';
  if (!isEnd(from) || !isEnd(to)) return null;
  return {
    line: {
      key: 'terminal.billing.key.capacity_changed',
      params: { from: from ?? 'default', to: to ?? 'default' },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.key.label_changed` (#5203) — an operator renamed (or named, or cleared) an
 * org key's route-pin handle. Reads `output` (`BillingKeyLabelChangedOutputSchema`):
 * each end is a string or `null` (unnamed). A row whose ends are neither renders
 * nothing, never a guess.
 */
function renderBillingKeyLabelChanged(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const out = asRecord(row.output);
  const from = out?.['from'];
  const to = out?.['to'];
  const isEnd = (v: unknown): v is string | null => v === null || typeof v === 'string';
  if (!isEnd(from) || !isEnd(to)) return null;
  return {
    line: {
      key: 'terminal.billing.key.label_changed',
      params: { from: from ?? 'unnamed', to: to ?? 'unnamed' },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.spend_cap.set` (`billing.ts`) — an operator explicitly moved
 * their monthly cap. `input.capUsd` is on every writer.
 */
function renderBillingSpendCapSet(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const capUsd = numOr(asRecord(row.input)?.['capUsd'], 0);
  return {
    line: { key: 'terminal.billing.spend_cap.set', params: { capUsd } },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/**
 * `billing.checkout.started` (`billing.ts`) — a seat purchase was
 * STARTED (intent, not money; see the kind's own doc). Rendered anyway: a
 * deliberate operator action with `tier`/`quantity` on the row, not a sync.
 */
function renderBillingCheckoutStarted(ctx: TerminalRenderContext): TerminalRendered | null {
  const { row } = ctx;
  const rec = asRecord(row.input);
  return {
    line: {
      key: 'terminal.billing.checkout.started',
      params: {
        tier: strOr(rec?.['tier'], 'unknown'),
        quantity: numOr(rec?.['quantity'], 1),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

// `billing.llm_pref.set` / `billing.llm_pref.cleared`: OMIT. A member's own
// personal model-default preference (`personal-plane.ts`,`:441`) — a UI
// setting, not a fact an operator scans the console for; there is no
// operator-visible consequence beyond "my own future turns use a different
// default", which the settings page itself already shows the member who set it.

// `account.seats_synced`: OMIT. A Stripe `customer.subscription.*` webhook
// reflecting seats/tier onto the account (`stripe-events.lane.ts`) — a
// system sync of state Stripe already owns, matching this family's other
// `_synced` omissions (`billing.key.limits_changed`). A seat DECREASE also
// drains excess boxes non-destructively, which is exactly the kind of detail
// worth a dedicated line if this ever earns one — not today's ask.

export const TERMINAL_RENDERERS_LOOP = terminalRendererGroup({
  'session.boot': { level: 'info', actor: 'coder', render: renderSessionBoot },
  'model.call': { level: 'info', actor: 'coder', render: renderModelCall },
  'model.step': 'omit',
  'model.reasoning': 'omit',
  'llm.failover': { level: 'decision', actor: 'system', render: renderLlmFailover },
  'tool.call.pending': { level: 'info', actor: 'coder', render: renderToolCallPending },
  'tool.call.ok': { level: 'ok', actor: 'coder', render: renderToolCallOk },
  'tool.call.err': { level: 'fail', actor: 'coder', render: renderToolCallErr },
  'mcp_tool.call.pending': { level: 'info', actor: 'mcp', render: renderMcpToolCallPending },
  'mcp_tool.call.ok': { level: 'ok', actor: 'mcp', render: renderMcpToolCallOk },
  'mcp_tool.call.err': { level: 'fail', actor: 'mcp', render: renderMcpToolCallErr },
  'policy.gate.refused': { level: 'decision', actor: 'system', render: renderPolicyGateRefused },
  'escalation.fired': { level: 'decision', actor: 'system', render: renderEscalationFired },
  // OMIT (plan 101 N3): a repeat told nobody — its first telling already has its
  // `escalation.fired` line, and a console line per re-fire is the flood this kind
  // exists to stop. The row stays on the chain for anyone counting re-fires.
  'escalation.repeated': 'omit',
  'handoff.fired': { level: 'decision', actor: 'system', render: renderHandoffFired },
  'email.sent': { level: 'ok', actor: 'system', render: renderEmailSent },
  'email.route_missing': { level: 'warn', actor: 'system', render: renderEmailRouteMissing },
  'email.failed': { level: 'warn', actor: 'system', render: renderEmailFailed },
  'notification.suppressed': {
    level: 'warn',
    actor: 'system',
    render: renderNotificationSuppressed,
  },
  // OMIT (#6358): a waiting email NOT sent because the member switched it off, nothing
  // could receive it, or its ask was answered first. None is a maintainer action. A
  // bounced address, the one a maintainer must act on, is `notification.suppressed` above.
  'email.deferred_refused': 'omit',
  'push.target_pruned': 'omit', // OMIT (#3956): a device prune is not a maintainer action.
  'account.paused': { level: 'warn', actor: 'system', render: renderAccountPaused },
  'account.resumed': { level: 'ok', actor: 'system', render: renderAccountResumed },
  'billing.metered': 'omit',
  'billing.key.captured': { level: 'ok', actor: 'system', render: renderBillingKeyCaptured },
  'billing.key.revoked': { level: 'ok', actor: 'system', render: renderBillingKeyRevoked },
  'billing.key.verified': { level: 'ok', actor: 'system', render: renderBillingKeyVerified },
  'billing.key.invalid': { level: 'warn', actor: 'system', render: renderBillingKeyInvalid },
  'billing.key.transport_failed': {
    level: 'warn',
    actor: 'system',
    render: renderBillingKeyTransportFailed,
  },
  'billing.key.model_parked': {
    level: 'warn',
    actor: 'system',
    render: renderBillingKeyModelParked,
  },
  'billing.key.rotated': { level: 'ok', actor: 'system', render: renderBillingKeyRotated },
  'billing.key.kind_changed': {
    level: 'info',
    actor: 'system',
    render: renderBillingKeyKindChanged,
  },
  'billing.key.status_changed': {
    level: 'info',
    actor: 'system',
    render: renderBillingKeyStatusChanged,
  },
  'billing.key.priority_changed': {
    level: 'info',
    actor: 'system',
    render: renderBillingKeyPriorityChanged,
  },
  'billing.key.capacity_changed': {
    level: 'info',
    actor: 'system',
    render: renderBillingKeyCapacityChanged,
  },
  'billing.key.label_changed': {
    level: 'info',
    actor: 'system',
    render: renderBillingKeyLabelChanged,
  },
  // #5116: `billing.key_outage.opened` / `.closed` — kept on the chain for anyone
  // counting re-opens, but the customer-facing notice is the row's OWN renderer
  // (#5119, #5120, #5121); a per-row terminal line would re-emit a notice the
  // customer already read.
  'billing.key_outage.opened': 'omit',
  'billing.key_outage.closed': 'omit',
  // #5118: `billing.key_outage.picked` — the outage's CLOSE record (`reason: 'picked'`)
  // and the row's own renderer is the customer-facing notice (#5119, #5121); a per-row
  // terminal line would re-emit the resolution the customer just acted on. Same family
  // as the `opened` / `closed` omissions above.
  'billing.key_outage.picked': 'omit',
  'billing.spend_cap.set': { level: 'info', actor: 'system', render: renderBillingSpendCapSet },
  'billing.checkout.started': {
    level: 'info',
    actor: 'system',
    render: renderBillingCheckoutStarted,
  },
  'billing.llm_pref.set': 'omit',
  'billing.llm_pref.cleared': 'omit',
  'account.seats_synced': 'omit',
  // #7662: the two billing-ownership refusals (`./loop-billing-ownership.ts`).
  ...TERMINAL_RENDERERS_BILLING_OWNERSHIP,
  ...TERMINAL_RENDERERS_KEY_MODELS,
});
